// R6 — Discover modal for triple-card rewards.
// S3 — Generalized to also drive hero-power discover. Two prop shapes:
//
//   Legacy (triples — unchanged):
//     pendingTripleReward: { tier } | null
//     discoverOptions: Card[]
//     onClaim: (card) => void
//
//   Generalized (hero-power discover and future cases):
//     open: boolean
//     options: Card[]
//     onSelect: (card) => void
//     headerCopy?: { eyebrow, title, hint }
//
// The component picks whichever shape is present. Callers should NOT
// mix both — if `open` is provided, the generalized path wins.
//
// Visual treatment unchanged: full-screen dim backdrop, three large
// CardTiles in a row (stacked on mobile), each wrapped in a sodium
// glow ring. The tavern stays mounted underneath; TavernPage pauses
// the timer while EITHER discover is open.
//
// Keyboard support: ←/→ cycle focus, Enter claims focused card.

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Card } from '../game/data/cards/types';
import { CardTile } from './CardTile';

export interface DiscoverHeaderCopy {
  /** Small uppercase eyebrow above the title — e.g. "triple reward". */
  eyebrow: string;
  /** Big title — e.g. "Discover · Tier 2". */
  title: string;
  /** Subline hint — e.g. "pick one · arrows + enter, or tap". */
  hint?: string;
}

interface DiscoverModalProps {
  /* --- legacy triple-reward path (R6) --- */
  /** When non-null, the modal is visible. */
  pendingTripleReward?: { tier: number } | null;
  /** The three card choices for the triple. */
  discoverOptions?: Card[];
  /** Selecting a card clears `pendingTripleReward` in the store. */
  onClaim?: (card: Card) => void;

  /* --- generalized path (S3 hero-power discover, future uses) --- */
  /** Visible when true. Takes precedence over the legacy path. */
  open?: boolean;
  /** Card choices (3 entries). */
  options?: Card[];
  /** Selection callback. */
  onSelect?: (card: Card) => void;
  /** Custom header text. */
  headerCopy?: DiscoverHeaderCopy;
}

export function DiscoverModal(props: DiscoverModalProps) {
  // Resolve which prop shape is being used. Generalized wins if `open`
  // is explicitly provided.
  const generalized = typeof props.open === 'boolean';

  const open = generalized
    ? props.open === true && (props.options?.length ?? 0) > 0
    : props.pendingTripleReward !== null &&
      props.pendingTripleReward !== undefined &&
      (props.discoverOptions?.length ?? 0) > 0;

  const options: Card[] = generalized
    ? props.options ?? []
    : props.discoverOptions ?? [];

  const onPick = (card: Card) => {
    if (generalized) props.onSelect?.(card);
    else props.onClaim?.(card);
  };

  // Header copy. Triple-reward defaults preserved 1:1 with the R6 build.
  const eyebrow = props.headerCopy?.eyebrow ?? 'triple reward';
  const title =
    props.headerCopy?.title ??
    (props.pendingTripleReward
      ? `Discover · Tier ${props.pendingTripleReward.tier}`
      : 'Discover');
  const hint = props.headerCopy?.hint ?? 'pick one · arrows + enter, or tap';

  const [focusIdx, setFocusIdx] = useState(0);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Reset focus to the first card whenever the modal opens or its
  // backing identity changes (tier change for triples, or hero-power
  // re-roll).
  useEffect(() => {
    if (open) {
      setFocusIdx(0);
      const id = window.setTimeout(() => {
        buttonRefs.current[0]?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [open, title]);

  // Keep DOM focus in sync with state-driven focus.
  useEffect(() => {
    if (!open) return;
    buttonRefs.current[focusIdx]?.focus();
  }, [focusIdx, open]);

  // Keyboard navigation. Escape auto-picks the focused card — discover
  // is non-dismissable per design (the player MUST claim a triple reward
  // before resuming the tavern), so ESC routes to the same selection
  // path as Enter rather than closing the modal silently.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusIdx((i) => (i - 1 + options.length) % options.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusIdx((i) => (i + 1) % options.length);
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
        e.preventDefault();
        const choice = options[focusIdx];
        if (choice) onPick(choice);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusIdx, options]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="discover-modal"
          className="pt-safe pb-safe fixed inset-0 z-50 flex items-center justify-center px-md py-md md:px-lg md:py-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          role="dialog"
          aria-modal="true"
          aria-label="discover a card"
        >
          {/* Backdrop — click matches ESC: auto-pick the focused card so the
              flow always advances. The modal is non-dismissable by design
              (a triple reward must be claimed before resuming the tavern). */}
          <button
            aria-label="claim focused card"
            onClick={() => {
              const choice = options[focusIdx];
              if (choice) onPick(choice);
            }}
            className="absolute inset-0 cursor-default bg-asphalt/85 backdrop-blur-sm"
          />
          {/* Card */}
          <motion.div
            initial={{ y: 16, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 8, scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="relative w-full max-w-md rounded-lg border bg-stucco1 px-md py-lg md:max-w-3xl md:px-xl md:py-xl"
            style={{ borderColor: 'rgba(255,138,61,0.25)' }}
          >
            <header className="mb-md flex flex-col items-center gap-xs md:mb-lg">
              <span className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
                {eyebrow}
              </span>
              <h2 className="font-emo text-[20px] uppercase tracking-[0.05em] text-bone md:text-[28px] lg:text-[32px]">
                {title}
              </h2>
              {hint && (
                <p className="text-center font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
                  {hint}
                </p>
              )}
            </header>
            <div className="grid grid-cols-3 gap-sm md:gap-lg">
              {options.map((card, i) => {
                const isFocused = i === focusIdx;
                return (
                  <button
                    key={card.instanceId ?? `${card.id}-${i}`}
                    ref={(el) => {
                      buttonRefs.current[i] = el;
                    }}
                    onClick={() => onPick(card)}
                    onMouseEnter={() => setFocusIdx(i)}
                    onFocus={() => setFocusIdx(i)}
                    className={`group relative rounded-md text-left transition-transform focus:outline-none ${
                      isFocused ? 'scale-[1.03] ring-2 ring-sodium shadow-sodium' : 'ring-1 ring-sodium/30'
                    }`}
                    aria-label={`pick ${card.name}`}
                  >
                    {/* CardTile inside — pass no onClick so the wrapper button captures the gesture */}
                    <CardTile card={card} selected={isFocused} />
                  </button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
